import { SubscriptionPlans, type Subscription } from "@prisma/client";

export const CHECKOUT_PROVIDER_FLAG_KEY = "checkout-provider-test";
export const CHECKOUT_PROVIDER_STORAGE_KEY = "mf_checkout_provider";
/** Wait this long for PostHog before falling back so CTAs are not stuck disabled. */
export const CHECKOUT_PROVIDER_FLAG_WAIT_MS = 2000;

export type CheckoutProvider = "paddle" | "stripe";

export const isCheckoutProvider = (value: unknown): value is CheckoutProvider =>
  value === "paddle" || value === "stripe";

export const parseCheckoutProviderParam = (
  value: unknown
): CheckoutProvider | null => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return isCheckoutProvider(normalized) ? normalized : null;
};

export type ResolveCheckoutProviderInput = {
  /** Paid subscription locks the billing provider. */
  subscription?: Pick<Subscription, "provider" | "plan"> | null;
  /** `?checkout=stripe|paddle` — sticky override for QA. */
  urlOverride?: CheckoutProvider | null;
  /** Prior sticky assignment from localStorage. */
  sticky?: CheckoutProvider | null;
  /** PostHog multivariable flag variant. */
  flagVariant?: string | boolean | null | undefined;
};

/**
 * Resolve which payment provider handles new Packages checkouts.
 * Order: paid lock → URL override → sticky → PostHog → default paddle.
 */
export const resolveCheckoutProvider = ({
  subscription,
  urlOverride = null,
  sticky = null,
  flagVariant,
}: ResolveCheckoutProviderInput): CheckoutProvider => {
  if (
    subscription &&
    subscription.plan !== SubscriptionPlans.free &&
    isCheckoutProvider(subscription.provider)
  ) {
    return subscription.provider;
  }

  if (urlOverride) {
    return urlOverride;
  }

  if (sticky) {
    return sticky;
  }

  if (typeof flagVariant === "string" && isCheckoutProvider(flagVariant)) {
    return flagVariant;
  }

  return "paddle";
};

/** True when sticky storage should be written (URL override or first flag assign). */
export const shouldPersistCheckoutProvider = ({
  subscription,
  urlOverride: _urlOverride,
}: {
  subscription?: Pick<Subscription, "plan"> | null;
  urlOverride?: CheckoutProvider | null;
}): boolean => {
  if (subscription && subscription.plan !== SubscriptionPlans.free) {
    return false;
  }
  return true;
};

export type IsCheckoutProviderReadyInput = {
  hydrated: boolean;
  routerReady: boolean;
  flagReady: boolean;
  urlOverride?: CheckoutProvider | null;
  sticky?: CheckoutProvider | null;
  flagWaitExpired?: boolean;
};

/**
 * Gate plan CTAs until client hydration + router query are ready, and either
 * PostHog/URL/sticky resolved or the flag wait timed out.
 */
export const isCheckoutProviderReady = ({
  hydrated,
  routerReady,
  flagReady,
  urlOverride = null,
  sticky = null,
  flagWaitExpired = false,
}: IsCheckoutProviderReadyInput): boolean => {
  if (!hydrated || !routerReady) {
    return false;
  }
  return flagReady || !!urlOverride || !!sticky || flagWaitExpired;
};
